/**
 * Ink Theme — Paean Family visual system.
 *
 * The Paean ecosystem consists of three sibling CLIs that share a common
 * tonal palette and a "trio sigil" while keeping individual hero glyphs:
 *
 *   ⌬   Zero        — the multi-model Core
 *   ∩   Paean       — the bridge to AI workflows  (this CLI)
 *   ∞   Articulate  — the sovereign terminal loop
 *
 * Anywhere we want to nod to the family without stealing focus from the
 * hero glyph, render the sigil triplet `⌬ · ∩ · ∞` in the muted accent.
 */

// True-color hex values — preferred on modern terminals.
export const COLORS = {
    primary: '#0077FF',      // Paean Blue (canonical hero)
    primaryDim: '#007799',   // Paean Deep Teal (supporting structure)
    accent: '#00F0FF',       // Paean Cyan (highlight only — used sparingly)
    secondary: '#8b5cf6',    // Violet (assistant voice)
    success: '#10b981',      // Emerald
    warning: '#f59e0b',      // Amber
    error: '#ef4444',        // Red
    muted: '#71717a',        // Gray
    border: '#333333',       // Dark border
    diffAdded: '#1a3a1a',    // Diff added bg
    diffRemoved: '#3a1a1a',  // Diff removed bg
};

// Named-color fallbacks (16-color terminals, low-color SSH, CI logs, etc.)
export const INK_COLORS = {
    primary: 'cyan' as const,
    secondary: 'magenta' as const,
    success: 'green' as const,
    warning: 'yellow' as const,
    error: 'red' as const,
    muted: 'gray' as const,
};

/**
 * ASCII Logo — three-row arch lockup.
 *
 *   ╭─────╮
 *   │ ╱∩╲ │     ← apex: Paean arch with quill descenders
 *   ╰─────╯
 *
 * The arch (∩) is the brand mark; the surrounding box implies a portal /
 * gateway, reinforcing Paean's "bridge" positioning. Box-drawing chars
 * connect seamlessly across rows even on minimal-spacing terminals.
 */
export type LogoSegment = { text: string; accent?: boolean };
export const ASCII_LOGO: LogoSegment[][] = [
    [{ text: '  ╭─────╮' }],
    [{ text: '  │ ' }, { text: '╱∩╲', accent: true }, { text: ' │' }],
    [{ text: '  ╰──┴──╯' }],
];

// Compact one-line lockup used when terminal width is constrained.
export const COMPACT_LOGO = '∩ Paean';

/**
 * Family sigil — render in COLORS.muted alongside the hero mark to
 * telegraph membership in the Paean ecosystem without competing for
 * attention.
 */
export const FAMILY_SIGIL = '⌬ · ∩ · ∞';
export const FAMILY_TAG = 'Paean Family';

// Brand symbols — single-width chars so layout math stays predictable.
export const SYMBOLS = {
    arch: '∩',          // Hero brand mark
    archSolid: '⌓',     // Filled arch (for strong accents)
    prompt: '❯',
    promptDouble: '❯❯',
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'ℹ',
    mcp: '⚡',
    tool: '⚙',
    thinking: '◌',
    sparklet: '✦',
    bullet: '•',
    bulletSm: '·',
    spinner: ['◐', '◓', '◑', '◒'],
    chevronRight: '›',
    chevronDown: '⌄',
    ellipsis: '…',
    dash: '─',
    vertLine: '│',
    cornerTL: '╭',
    cornerTR: '╮',
    cornerBL: '╰',
    cornerBR: '╯',
    queue: '⏳',
    remote: '⬡',
    wechat: '💬',
    pin: '◆',
};

// Tool status glyphs — single source of truth for tool lifecycle states.
export const TOOL_STATUS = {
    SUCCESS: '✓',
    PENDING: '○',
    EXECUTING: '◌',
    ERROR: '✗',
    CANCELED: '–',
} as const;

// Animation frames for the thinking indicator (quadrant rotation).
export const THINKING_FRAMES = ['◐', '◓', '◑', '◒'];

/**
 * Rotating thinking messages — kept short, contemplative, and on-brand
 * (Paean = "song of praise / response"). These rotate on a 3s cadence
 * via the Spinner component.
 */
export const THINKING_MESSAGES = [
    'Composing the response…',
    'Tuning the chorus of tools…',
    'Threading context together…',
    'Listening to the codebase…',
    'Sketching the next step…',
    'Refining intent…',
    'Weighing approaches…',
    'Almost there…',
];

// Layout constants
export const SEPARATOR_CHAR = '─';
export const TOOL_RESULT_MAX_LINES = 12;
export const DIFF_MAX_LINES = 15;
export const SHELL_OUTPUT_MAX_LINES = 10;

/** Minimum width before the layout collapses to a compact rendering. */
export const NARROW_TERMINAL_THRESHOLD = 60;
